import { getNodeAttributes } from '@tiptap/core';
import { ReactComponent as DeleteColumnIcon } from 'assets/icons/delete_column.svg';
import { ReactComponent as DeleteRowIcon } from 'assets/icons/delete_row.svg';
import { ReactComponent as InsertColumnLeftIcon } from 'assets/icons/insert_column_left.svg';
import { ReactComponent as InsertColumnRightIcon } from 'assets/icons/insert_column_right.svg';
import { ReactComponent as InsertRowAboveIcon } from 'assets/icons/insert_row_above.svg';
import { ReactComponent as InsertRowBelowIcon } from 'assets/icons/insert_row_below.svg';
import { TEXT_ALIGNMENT } from 'constants/fonts';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { Bold, Italic, Underline } from 'modules/common/components/FontStyle';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { preventDefault } from 'utils/event';
import { ModifyFontSizeOperation } from 'widgets/TextBasedWidgets/common/extensions';
import { LinkMenu } from 'widgets/sdk';

import { TextWidgetTag } from '../../common/TextBasedWidgets.types';
import {
  FontFamily,
  FontSize,
  LineHeight,
  OrderedList,
  TextAlignment,
  TextColor,
  UnOrderedList,
} from '../../common/toolbar';
import { fireMPonTableDelete, fireMPonTableInsert, isList, toggleList } from '../TableWidget.helpers';
import { CellColor } from './CellColor';
import { InsertCheckboxButton } from './InsertCheckboxButton';
import { getSelectedNode, getTableElementTypeFromString } from './TableWidgetToolbar.helpers';
import { ToolbarProps } from './TableWidgetToolbar.types';
import { VerticalAlignment } from './VerticalAlignment';

const INSERT_OPTIONS = [
  {
    value: 'insertColumnRight',
    label: 'insertOptions.colRight',
    icon: <InsertColumnRightIcon />,
  },
  {
    value: 'insertColumnLeft',
    label: 'insertOptions.colLeft',
    icon: <InsertColumnLeftIcon />,
  },
  {
    value: 'insertRowAbove',
    label: 'insertOptions.rowAbove',
    icon: <InsertRowAboveIcon />,
  },
  {
    value: 'insertRowBelow',
    label: 'insertOptions.rowBelow',
    icon: <InsertRowBelowIcon />,
  },
];

const DELETE_OPTIONS = [
  {
    value: 'deleteColumn',
    label: 'deleteOptions.col',
    icon: <DeleteColumnIcon />,
  },
  {
    value: 'deleteRow',
    label: 'deleteOptions.row',
    icon: <DeleteRowIcon />,
  },
];

const MAX_LINE_HEIGHT = 10;

export const EditToolbar = ({ editor, dispatchUpdateWidget }: ToolbarProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'tableToolbar' });
  const debounceDispatchUpdateWidget = useDebouncedCallback(dispatchUpdateWidget, 200);
  const textStyle = editor?.getAttributes('textStyle');

  // Get node attributes based on tag type
  const nodeAttributes = editor?.state && getNodeAttributes(editor.state, TextWidgetTag.Paragraph);
  const cellAttributes = editor?.state && getNodeAttributes(editor.state, 'tableCell');
  const headerAttributes = editor?.state && getNodeAttributes(editor.state, 'tableHeader');
  const isBold = !!editor?.isActive('bold');
  const isItalic = !!editor?.isActive('italic');
  const isUnderline = !!editor?.isActive('underline');
  const fontFamily = textStyle?.fontFamily;
  const fontSize = String(parseInt(textStyle?.fontSize) ?? '');
  const fontColor = textStyle?.color;
  const textAlign = nodeAttributes?.textAlign;
  const verticalAlignment = cellAttributes?.verticalAlignment;
  const lineHeight = nodeAttributes?.lineHeight;
  const isLink = !!editor?.isActive('link');
  const link = editor?.getAttributes('link')?.href || '';
  const selectedText = editor?.state.doc.textBetween(editor?.state.selection.from, editor?.state.selection.to, ' ');

  const $from = editor.view.state.selection.$from;
  let parentList: ProseMirrorNode = $from.node($from.depth);
  for (let depth = $from.depth - 1; depth >= 1 && parentList && !isList(editor, parentList); depth--) {
    parentList = $from.node(depth);
  }

  const isOrderedList = parentList?.type === editor.schema.nodes.orderedList;
  const isUnOrderedList = parentList?.type === editor.schema.nodes.bulletList;
  const cellColor = cellAttributes?.backgroundColor;
  const headerColor = headerAttributes?.backgroundColor;
  const textStyleForColorChecker = useMemo(
    () => ({
      fontFamily: fontFamily ?? '',
      fontSize,
      fontWeight: isBold ? 'bold' : 'normal',
    }),
    [fontFamily, fontSize, isBold],
  );
  const node = getSelectedNode(editor);
  const targetRectForColorChecker = node?.getBoundingClientRect();

  const onSetCellColor = useCallback(
    (color: string) => {
      editor
        .chain()
        .focus()
        .setCellAttribute('backgroundColor', color)
        .updateAttributes('tableHeader', { backgroundColor: color })
        .run();
      dispatchUpdateWidget();
    },
    [editor, dispatchUpdateWidget],
  );

  const onSetBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const onSetItalic = useCallback(() => {
    editor.chain().focus().toggleItalic().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const onSetUnderline = useCallback(() => {
    editor.chain().focus().toggleUnderline().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const onSetFontFamily = useCallback(
    (fontFamily: string) => {
      if (!fontFamily) return;
      editor.chain().focus().setFontFamily(fontFamily).run();
      dispatchUpdateWidget();
    },
    [editor, dispatchUpdateWidget],
  );

  const onSetFontColor = useCallback(
    (color: string) => {
      editor.chain().focus().setColor(color).run();
      debounceDispatchUpdateWidget();
    },
    [editor, debounceDispatchUpdateWidget],
  );

  const onSetFontSize = useCallback(
    (newFontSize: number) => {
      if (!newFontSize) return;

      editor.chain().focus().setFontSize(`${newFontSize}px`).run();
      dispatchUpdateWidget();
    },
    [editor, dispatchUpdateWidget],
  );

  const onIncrementFontSize = useCallback(() => {
    editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Increment).run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const onDecrementFontSize = useCallback(() => {
    editor.chain().focus().modifyFontSizeByOperation(ModifyFontSizeOperation.Decrement).run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const onSetTextAlign = useCallback(
    (alignment: TEXT_ALIGNMENT) => {
      if (!alignment) return;

      editor.chain().focus().updateAttributes('paragraph', { textAlign: alignment }).run();
      dispatchUpdateWidget();
    },
    [editor, dispatchUpdateWidget],
  );

  const onSetVerticalAlignment = useCallback(
    (verticalAlignment: string) => {
      editor
        .chain()
        .focus()
        .setCellAttribute('verticalAlignment', verticalAlignment)
        .updateAttributes('tableHeader', { verticalAlignment })
        .run();
      dispatchUpdateWidget();
    },
    [editor, dispatchUpdateWidget],
  );

  const onSetLineHeight = useCallback(
    (lineHeight: string) => {
      editor.chain().focus().updateAttributes('paragraph', { lineHeight }).run();
      dispatchUpdateWidget();
    },
    [editor, dispatchUpdateWidget],
  );

  const onSetLink = useCallback(
    (newUrl: string | null) => {
      if (newUrl === null) return;

      if (newUrl === '') {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .command(({ commands }) => {
            if (isUnderline) {
              return commands.toggleUnderline();
            }
            return false;
          })
          .run();
        return;
      }

      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .command(({ commands }) => {
          if (!isUnderline) {
            return commands.toggleUnderline();
          }
          return false;
        })
        .setLink({ href: newUrl })
        .run();
    },
    [editor, isUnderline],
  );

  const onToggleOrderedList = useCallback(() => {
    const NUMBERED_LIST_TYPE = editor.schema.nodes.orderedList;
    toggleList(editor, NUMBERED_LIST_TYPE);
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const onToggleBulletedList = useCallback(() => {
    const BULLETED_LIST_TYPE = editor.schema.nodes.bulletList;
    toggleList(editor, BULLETED_LIST_TYPE);
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const insertRowBelow = useCallback(() => {
    editor.chain().focus().addRowAfter().updateRowAttrsBelow().updateAllCellBorderStyle().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const insertRowAbove = useCallback(() => {
    editor.chain().focus().addRowBefore().updateRowAttrsAbove().updateAllCellBorderStyle().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const insertColumnRight = useCallback(() => {
    editor.chain().focus().addColumnAfter().updateColumnAttrsRight().updateAllCellBorderStyle().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const insertColumnLeft = useCallback(() => {
    editor.chain().focus().addColumnBefore().updateColumnAttrsLeft().updateAllCellBorderStyle().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const deleteRow = useCallback(() => {
    editor.chain().focus().deleteRow().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const deleteColumn = useCallback(() => {
    editor.chain().focus().deleteColumn().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  const insertCheckbox = useCallback(() => {
    editor?.chain().focus().insertCheckbox().run();
    dispatchUpdateWidget();
  }, [editor, dispatchUpdateWidget]);

  // return focus back to selection
  const focusEditor = useCallback(() => editor.commands.focus(), [editor]);

  const handleInsert = useCallback(
    (insertOption: string) => {
      switch (insertOption) {
        case 'insertColumnLeft':
          insertColumnLeft();
          break;
        case 'insertColumnRight':
          insertColumnRight();
          break;
        case 'insertRowAbove':
          insertRowAbove();
          break;
        case 'insertRowBelow':
          insertRowBelow();
          break;
      }

      //  get insert type from insertOption cases above and fire mixpanel event if it is Column or Row
      const insertType = getTableElementTypeFromString(insertOption, 1);
      if (insertType) {
        fireMPonTableInsert(insertType);
      }
    },
    [insertColumnLeft, insertColumnRight, insertRowAbove, insertRowBelow],
  );

  const handleDelete = useCallback(
    (deleteOption: string) => {
      switch (deleteOption) {
        case 'deleteColumn':
          deleteColumn();
          break;
        case 'deleteRow':
          deleteRow();
          break;
      }

      // get delete type from deleteOption cases above and fire mixpanel event if it is Column or Row
      const deleteType = getTableElementTypeFromString(deleteOption, 1);
      if (deleteType) {
        fireMPonTableDelete(deleteType);
      }
    },
    [deleteColumn, deleteRow],
  );

  return (
    <>
      <CellColor value={(cellColor || headerColor) ?? ''} onChange={onSetCellColor} onClose={focusEditor} />
      <TextColor
        color={fontColor ?? ''}
        onChange={onSetFontColor}
        onClose={focusEditor}
        colorCheckerProps={{
          color: fontColor ?? '',
          textStyle: textStyleForColorChecker,
          targetRect: targetRectForColorChecker,
        }}
      />
      <FontFamily value={fontFamily} onChange={onSetFontFamily} onClose={focusEditor} />
      <FontSize
        value={fontSize}
        onChange={onSetFontSize}
        onDecrement={onDecrementFontSize}
        onIncrement={onIncrementFontSize}
      />
      <Bold fontFamily={fontFamily} isActive={isBold} onChange={onSetBold} />
      <Italic fontFamily={fontFamily} isActive={isItalic} onChange={onSetItalic} />
      <Underline isActive={isUnderline} onChange={onSetUnderline} />
      <ToolbarDivider />
      <TextAlignment value={textAlign ?? TEXT_ALIGNMENT.left} onChange={onSetTextAlign} />
      <VerticalAlignment value={verticalAlignment} onChange={onSetVerticalAlignment} />
      <LineHeight value={lineHeight} onChange={onSetLineHeight} maxLineHeight={MAX_LINE_HEIGHT} />
      <ToolbarDivider />
      <UnOrderedList isActive={isUnOrderedList} onChange={onToggleBulletedList} />
      <OrderedList isActive={isOrderedList} onChange={onToggleOrderedList} />
      <ToolbarDivider />
      <InsertCheckboxButton onClick={insertCheckbox} />
      <ToolbarDivider />
      <LinkMenu value={link} isActive={isLink} text={selectedText} onChange={onSetLink} onMouseDown={preventDefault} />
      <ToolbarDivider />

      <DropdownPopover
        options={generateTranslatedOptions(t, INSERT_OPTIONS)}
        buttonVariant={'ghost-with-active'}
        focusShadow={{}}
        triggerProps={{ w: 'fit-content', size: 'sm' }}
        label={t('insertOptions.label')}
        onSelect={handleInsert}
        w={'15rem'}
        justifyContent={'flex-start'}
        closeOnSelect
        returnFocusOnClose={false}
        useLabelInsteadOfSelectedLabel
      />

      <DropdownPopover
        options={generateTranslatedOptions(t, DELETE_OPTIONS)}
        buttonVariant={'ghost-with-active'}
        focusShadow={{}}
        triggerProps={{ w: 'fit-content', size: 'sm' }}
        label={t('deleteOptions.label')}
        onSelect={handleDelete}
        w={'15rem'}
        justifyContent={'flex-start'}
        closeOnSelect
        returnFocusOnClose={false}
        useLabelInsteadOfSelectedLabel
      />
    </>
  );
};
